import { PartNoBg } from '$pages/serviceuser/Marketplace/Bookservice/Defaultwizard/styledComponents'
import { ArrowRightOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { StyledTable } from './styledComponents'
import { useTranslation } from 'react-i18next'
import BookedServiceLabels from '$components/Label/BookedServiceLabels'
import PartFileDownloadButton from '$components/FileDownload/PartFileDownloadButton'
import { calculateOrderPartPriceRange } from '$components/OrderPartsListTable/utils'
import { currencyRenderer, getColorLabel } from '$utils/i18n'
import { ValidationWrapper } from '$globalStyledComponents/Text'
import TruncateText from '$components/TruncateText'

const RequestedPartsTable = ({
  orderParts = [],
  loading,
  next,
  currentStep,
  orderPartsFormValues,
}) => {
  const { t } = useTranslation()

  const columns = [
    {
      title: t('Part No'),
      dataIndex: ['part', 'id_part_client'],
      render: (title) => <PartNoBg>{title}</PartNoBg>,
      width: '160px',
    },
    {
      title: t('Part Name'),
      render: (record) => {
        return (
          <TruncateText
            text={record.part.name}
            style={
              record.status_info === 'discarded'
                ? { textDecoration: 'line-through' }
                : {}
            }
          />
        )
      },
      ellipsis: true,
    },
    {
      title: t('Material'),
      render: (record) => {
        const materialName =
          record.service_provider_material?.material_name || t('unknown')
        return (
          <TruncateText
            text={`${materialName}${getColorLabel(record.selected_color, t)}`}
          />
        )
      },
      ellipsis: true,
    },
    {
      title: t('Amount'),
      dataIndex: 'amount',
      width: '7%',
    },
    {
      title: t('Dimensions'),
      render: (record) => (
        <div
          style={
            record.status_info === 'discarded'
              ? { textDecoration: 'line-through' }
              : {}
          }
        >
          {
            record.part.float_attributes.find(
              (attribute) => attribute.key === 'Width',
            )?.value
          }{' '}
          x{' '}
          {
            record.part.float_attributes.find(
              (attribute) => attribute.key === 'Height',
            )?.value
          }{' '}
          x{' '}
          {
            record.part.float_attributes.find(
              (attribute) => attribute.key === 'Depth',
            )?.value
          }
        </div>
      ),
    },
    {
      title: t('received_requests.offered_price'),
      render: (record) => {
        const editableOfferValues =
          orderPartsFormValues?.[record.id]?.['provider_offers'] &&
          Object.values(orderPartsFormValues?.[record.id]?.['provider_offers'])

        let priceRange
        const offersToCalculate = editableOfferValues?.length
          ? editableOfferValues
          : record.provider_offers

        if (offersToCalculate?.length) {
          priceRange = calculateOrderPartPriceRange(
            offersToCalculate.filter(
              (offer) => offer.amount && offer.base_price,
            ),
            record.client_accepted_offer,
          )
        }

        if (priceRange) {
          const { min, max } = priceRange
          if (min === max) {
            return (
              <ValidationWrapper $valid={min > 0}>
                {currencyRenderer(t)(min)}
              </ValidationWrapper>
            )
          }
          return (
            <ValidationWrapper $valid={min > 0}>
              {`${currencyRenderer(t)(min)} - ${currencyRenderer(t)(max)}`}
            </ValidationWrapper>
          )
        } else {
          return t('received_requests.not_yet_defined')
        }
      },
    },
    {
      title: 'Labels',
      render: (record) => {
        return (
          <>
            {record.status_info !== 'discarded' && (
              <BookedServiceLabels bookedServiceId={record.id} />
            )}
          </>
        )
      },
    },
    {
      title: '',
      fixed: 'right',
      render: (record) => {
        const handleClick = () => {
          next(record.id)
        }
        const hasProviderOffers =
          record.provider_offers?.length && record.provider_offers.length > 0

        return currentStep === 0 ? (
          hasProviderOffers ? (
            <div style={{ textAlign: 'right' }}>
              <Button shape="round" size="small" onClick={handleClick}>
                {t('received_requests.edit_offer')} <ArrowRightOutlined />
              </Button>
            </div>
          ) : (
            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                shape="round"
                size="small"
                onClick={handleClick}
                disabled={record.status_info === 'discarded'}
              >
                {t('received_requests.edit_request')} <ArrowRightOutlined />
              </Button>
            </div>
          )
        ) : (
          <PartFileDownloadButton
            shape="round"
            size="small"
            fileName={record.user_file_location}
          >
            {t('received_requests.download_stl_file')}
          </PartFileDownloadButton>
        )
      },
    },
  ]

  return (
    <StyledTable
      size="small"
      columns={columns}
      style={{ width: '100%' }}
      dataSource={orderParts}
      loading={loading}
      pagination={currentStep === 0 ? true : false}
      rowKey={(data) => data.id}
    />
  )
}

export default RequestedPartsTable
